import type { RuntimeConfig } from 'nuxt/schema';

import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { serializeError } from 'serialize-error';
import { watch } from 'vue';

import type { ErrorHandler } from '@/types/errorHandler';

import buildInfo from '@/buildInfo';
import { ConsoleErrorHandler } from '@/utils/errors/consoleErrorHandler';
import { serializeContext } from '@/utils/errors/serializeContext';

class ClientDatadogErrorHandler implements ErrorHandler {
  constructor(config: RuntimeConfig) {
    datadogLogs.init({
      site: 'datadoghq.com',
      clientToken: config.public.datadogClientToken,
      forwardErrorsToLogs: false,
      service: `${config.public.datadogService}-browser`,
      version: buildInfo.build,
      env: config.public.env,
    });
  }

  private log(status: 'error' | 'warn' | 'info', error: Error, additionalContext: object = {}) {
    datadogLogs.logger[status](error.message, { context: serializeContext(additionalContext), error: serializeError(error) }, error);
  }

  logError(error: Error, additionalContext: object = {}) {
    this.log('error', error, additionalContext);
  }

  logWarning(error: Error, additionalContext: object = {}) {
    this.log('warn', error, additionalContext);
  }

  logInfo(error: Error, additionalContext: object = {}) {
    this.log('info', error, additionalContext);
  }
}

export const getClientErrorHandler = (config: RuntimeConfig): ErrorHandler =>
  (config.public.datadogEnabled ? new ClientDatadogErrorHandler(config) : new ConsoleErrorHandler());

interface InitRumOptions {
  apiUrl: string;
  clusterCode: string | null;
  suppressSessionReplay: boolean;
}

interface BindRumUserOptions {
  getExternalId: () => string | null;
}

export const initRum = (config: RuntimeConfig, { apiUrl, clusterCode, suppressSessionReplay }: InitRumOptions) => {
  if (!config.public.datadogEnabled) {
    return;
  }

  datadogRum.init({
    applicationId: config.public.datadogApplicationId,
    clientToken: config.public.datadogClientToken,
    site: 'datadoghq.com',
    service: `${config.public.datadogService}-browser`,
    version: buildInfo.build,
    env: config.public.env,
    sessionSampleRate: 100,
    sessionReplaySampleRate: suppressSessionReplay ? 0 : 100,
    // The sample rate cannot hold this gate on its own: a session that already
    // drew `rum=1` into its `_dd_s` cookie keeps that tracking type, and the
    // recorder otherwise starts automatically with RUM. Starting manually makes
    // the explicit startSessionReplayRecording() call below the only way replay
    // can begin, whatever the cookie says.
    startSessionReplayRecordingManually: true,
    allowedTracingUrls: [apiUrl],
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask-user-input',
    // Action names fall back to the clicked element's text content, which on a
    // suppressed cluster can carry a student's name; send 'Masked Element'.
    enablePrivacyForActionName: suppressSessionReplay,
  });
  // Cluster identity as a queryable RUM facet — the traced URL shape moved from
  // subdomain to path, so dashboards filter on this rather than parsing the path.
  if (clusterCode) {
    datadogRum.setGlobalContextProperty('cluster_code', clusterCode);
  }
  else {
    // No cluster resolved → the RUM session is invisible to any cluster-filtered
    // dashboard/alert. Surface it via Datadog logs (datadogLogs is initialised
    // above when datadogEnabled, the only path that reaches here).
    datadogLogs.logger.warn('[datadog] cluster_code unavailable — RUM session unattributed');
  }
  // The suppression decision is itself a facet: without it a session carrying no
  // replay is indistinguishable from one whose recording silently broke.
  datadogRum.setGlobalContextProperty('session_replay_suppressed', suppressSessionReplay);
  if (!suppressSessionReplay) {
    // `force` because the `_dd_s` cookie pins a session's tracking type for its
    // lifetime: a session that began while suppression was on is "tracked without
    // replay", and an unforced call cannot upgrade it — replay would stay off
    // until the session renewed, up to 4h later. The suppressed branch never
    // reaches this call, so forcing here does not weaken the gate.
    datadogRum.startSessionReplayRecording({ force: true });
  }
};

export const bindRumUser = (config: RuntimeConfig, { getExternalId }: BindRumUserOptions) => {
  if (!config.public.datadogEnabled) {
    return;
  }

  watch(getExternalId, (id) => {
    if (id) {
      datadogRum.setUser({ id });
    }
    else {
      datadogRum.clearUser();
    }
  }, { immediate: true });
};
